import { apiCall, http } from '../http'
import { sleep } from '@/utils'
import type {
    Gcb2ChatListType,
    Gcb2ChatStatsItem,
    Gcb2OmnichannelChat,
    Gcb2OmnichannelMarkReadResponse,
    Gcb2OmnichannelMessagesResponse,
    Gcb2OmnichannelRealtimeSnapshotResponse,
    Gcb2OmnichannelSendResponse,
    Gcb2OmnichannelUnreadChatsCountResponse,
    Gcb2OmnichannelUnreadCountResponse,
    MessengerChat,
    DhChatContact,
    DhChatResolveJobResponse,
    DhChatSendFirstResponse,
    DhChatThread,
    DhChatThreadMessagesResponse,
    DhChatMedia,
    DhChatScope,
} from './types'

const CHAT_API_RETRY_ATTEMPTS = 3
const CHAT_API_RETRY_DELAY_MS = 1000

async function withApiResultRetry<T>(
    fn: () => Promise<{ data: T | null; error: any }>,
    attempts = CHAT_API_RETRY_ATTEMPTS,
    delayMs = CHAT_API_RETRY_DELAY_MS,
) {
    let last = await fn()
    for (let i = 1; i < attempts; i++) {
        if (!last.error) return last
        await sleep(delayMs)
        last = await fn()
    }
    return last
}

export interface GetChatsParams {
    limit: number
    offset: number
    activationId?: string
    projectId: string
}

export interface GetChatByIdParams {
    chatId: string
    activationId?: string
    projectId: string
}

/**
 * Получить все чаты проекта
 */
export async function getAllChats(params: { limit: number; offset: number; projectId: string }) {
    const url = `messenger/chat/all`
    const { data, error } = await apiCall<MessengerChat[]>(() =>
        http.get(url, {
            searchParams: {
                limit: params.limit.toString(),
                offset: params.offset.toString(),
                projectId: params.projectId,
            },
        })
    )
    return { data, error }
}

/**
 * Получить чаты с фильтрацией по activationId
 */
export async function getChats(params: GetChatsParams) {
    const url = `messenger/chat`
    const searchParams: Record<string, string> = {
        limit: params.limit.toString(),
        offset: params.offset.toString(),
        projectId: params.projectId,
    }
    if (params.activationId) {
        searchParams.activationId = params.activationId
    }
    const { data, error } = await apiCall<MessengerChat[]>(() =>
        http.get(url, { searchParams })
    )
    return { data, error }
}

/**
 * Добавить чат по номеру телефона
 */
export async function addChat(params: { phone: string; projectId: string }) {
    const url = `messenger/chat/add`
    const { data, error } = await apiCall<MessengerChat[]>(() =>
        http.post(url, {
            searchParams: {
                phone: params.phone.replace('+', ''),
                projectId: params.projectId,
            },
        })
    )
    return { data, error }
}

/**
 * Получить чат по ID
 */
export async function getChatById(params: GetChatByIdParams) {
    const url = `messenger/chat/${params.chatId}`
    const searchParams: Record<string, string> = {
        projectId: params.projectId,
    }
    if (params.activationId) {
        searchParams.activationId = params.activationId
    }
    const { data, error } = await apiCall<MessengerChat>(() =>
        http.get(url, { searchParams })
    )
    return { data, error }
}

/**
 * Создать новый чат
 */
export async function createChat(params: { phoneNumber: string; projectId: string }) {
    const url = `messenger/chat`
    const { data, error } = await apiCall<MessengerChat>(() =>
        http.post(url, {
            json: {
                phoneNumber: params.phoneNumber,
                projectId: params.projectId,
            },
        })
    )
    return { data, error }
}

/**
 * Найти чат по строке поиска
 */
export async function findChat(params: { findStr: string; projectId: string }) {
    const url = `messenger/chat/find/${params.findStr}`
    const { data, error } = await apiCall<MessengerChat>(() =>
        http.get(url, {
            searchParams: {
                projectId: params.projectId,
            },
        })
    )
    return { data, error }
}

export interface Gcb2ChatListParams {
    projectId: string
    limit?: number
    offset?: number
    search?: string
    type?: Gcb2ChatListType
    connectorId?: string
    phone?: string
    clientIds?: string[] | string
    unread?: boolean
}

export interface Gcb2ChatStatsParams {
    projectId: string
    search?: string
}

export interface Gcb2ChatMessagesParams {
    projectId: string
    chatId: string
    limit?: number
    beforeMessageId?: string
    beforeCreatedAt?: string
    search?: string
    dateFrom?: string
    dateTo?: string
    eventTypes?: string[]
}

export interface Gcb2ChatReadParams {
    projectId: string
    chatId: string
    lastVisibleMessageId?: string
}

export interface Gcb2ChatSendParams {
    projectId: string
    chatId: string
    text: string
    connectorId?: string
    retryMessageId?: string
    media?: { src: string; mimeType: string; originalName?: string } | { src: string; mimeType: string; originalName?: string }[]
}

export interface Gcb2ChatRealtimeSnapshotParams {
    projectId: string
    chatId: string
    messageId: string
}

/** POST GCB2/chat/list */
export async function getAllChatsOmnichannel(params: Gcb2ChatListParams) {
    const url = `GCB2/chat/list`
    const json: Record<string, unknown> = {
        projectId: params.projectId,
        limit: params.limit,
        offset: params.offset,
    }
    if (params.search) json.search = params.search
    if (params.type && params.type !== 'all') json.type = params.type
    if (params.connectorId) json.connectorId = params.connectorId
    if (params.phone) json.phone = params.phone
    if (params.clientIds) json.clientIds = params.clientIds
    if (params.unread) json.unread = true

    return withApiResultRetry(() =>
        apiCall<Gcb2OmnichannelChat[]>(() => http.post(url, { json })),
    )
}

/** POST GCB2/chat/stats */
export async function getChatStatsOmnichannel(params: Gcb2ChatStatsParams) {
    const url = `GCB2/chat/stats`
    const json: Record<string, unknown> = {
        projectId: params.projectId,
    }
    if (params.search) json.search = params.search

    return withApiResultRetry(() =>
        apiCall<Gcb2ChatStatsItem[]>(() => http.post(url, { json })),
    )
}

function unreadSearchParams(params: { projectId: string; connectorIds?: string[]; types?: string[] }) {
    const searchParams = new URLSearchParams()
    searchParams.set('projectId', params.projectId)
    params.connectorIds?.forEach((id) => searchParams.append('connectorIds', id))
    params.types?.forEach((type) => searchParams.append('types', type))
    return searchParams
}

/** GET GCB2/chat/unreadCount */
export async function getUnreadCountOmnichannel(params: {
    projectId: string
    connectorIds?: string[]
    types?: string[]
}) {
    const url = `GCB2/chat/unreadCount`
    const { data, error } = await apiCall<Gcb2OmnichannelUnreadCountResponse>(() =>
        http.get(url, { searchParams: unreadSearchParams(params) })
    )
    return { data, error }
}

/** GET GCB2/chat/unreadChatsCount */
export async function getUnreadChatsCountOmnichannel(params: {
    projectId: string
    connectorIds?: string[]
    types?: string[]
}) {
    const url = `GCB2/chat/unreadChatsCount`
    const { data, error } = await apiCall<Gcb2OmnichannelUnreadChatsCountResponse>(() =>
        http.get(url, { searchParams: unreadSearchParams(params) })
    )
    return { data, error }
}

/** POST GCB2/chat/messages */
export async function getByChatOmnichannel(params: Gcb2ChatMessagesParams) {
    const url = `GCB2/chat/messages`
    const json: Record<string, unknown> = {
        projectId: params.projectId,
        chatId: params.chatId,
    }
    if (params.limit != null) json.limit = params.limit
    if (params.beforeMessageId) json.beforeMessageId = params.beforeMessageId
    if (params.beforeCreatedAt) json.beforeCreatedAt = params.beforeCreatedAt
    if (params.search) json.search = params.search
    if (params.dateFrom) json.dateFrom = params.dateFrom
    if (params.dateTo) json.dateTo = params.dateTo
    if (params.eventTypes?.length) json.eventTypes = params.eventTypes

    const { data, error } = await apiCall<Gcb2OmnichannelMessagesResponse>(() =>
        http.post(url, { json })
    )
    return { data, error }
}

/** POST GCB2/chat/read */
export async function markChatReadOmnichannel(params: Gcb2ChatReadParams) {
    const url = `GCB2/chat/read`
    const { data, error } = await apiCall<Gcb2OmnichannelMarkReadResponse>(() =>
        http.post(url, {
            json: {
                projectId: params.projectId,
                chatId: params.chatId,
                lastVisibleMessageId: params.lastVisibleMessageId,
            },
        })
    )
    return { data, error }
}

/** POST GCB2/chat/realtimeSnapshot */
export async function getRealtimeSnapshotOmnichannel(params: Gcb2ChatRealtimeSnapshotParams) {
    const url = `GCB2/chat/realtimeSnapshot`
    const { data, error } = await apiCall<Gcb2OmnichannelRealtimeSnapshotResponse>(() =>
        http.post(url, {
            json: {
                projectId: params.projectId,
                chatId: params.chatId,
                messageId: params.messageId,
            },
        })
    )
    return { data, error }
}

/** POST GCB2/chat/send */
export async function sendMessageOmnichannel(params: Gcb2ChatSendParams) {
    const url = `GCB2/chat/send`
    const json: Record<string, unknown> = {
        projectId: params.projectId,
        chatId: params.chatId,
        connectorId: params.connectorId,
        retryMessageId: params.retryMessageId,
        text: params.text,
    }
    if (params.media) json.media = params.media
    const { data, error } = await apiCall<Gcb2OmnichannelSendResponse>(() =>
        http.post(url, { json })
    )
    return { data, error }
}

export interface DhChatThreadsParams {
    projectId: string
    limit?: number
    offset?: number
    search?: string
    connectorIds?: string[]
    types?: string[]
    unread?: boolean
    archived?: 'exclude' | 'only' | 'all'
    status?: 'new'
}

export interface DhChatThreadStateParams {
    projectId: string
    threadIds: string[]
    pinned?: boolean
    archived?: boolean
    manualUnread?: boolean
}

export interface DhChatThreadsReadParams {
    projectId: string
    threadIds: string[]
    read: boolean
}

export interface DhChatSendFirstParams {
    projectId: string
    connectorId: string
    phone?: string
    clientId?: string
    clientName?: string
    text?: string
    media?: DhChatMedia | DhChatMedia[]
}

/** POST GCB2/chat/threads */
export async function getChatThreads(params: DhChatThreadsParams) {
    const url = `GCB2/chat/threads`
    const json: Record<string, unknown> = {
        projectId: params.projectId,
        limit: params.limit,
        offset: params.offset,
    }
    if (params.search) json.search = params.search
    if (params.connectorIds?.length) json.connectorIds = params.connectorIds
    if (params.types?.length) json.types = params.types
    if (params.unread) json.unread = true
    if (params.archived) json.archived = params.archived
    if (params.status) json.status = params.status
    return apiCall<DhChatThread[]>(() => http.post(url, { json }))
}

/** GET GCB2/chat/thread */
export async function getChatThread(params: { projectId: string; threadId: string }) {
    const url = `GCB2/chat/thread`
    return apiCall<DhChatThread>(() =>
        http.get(url, {
            searchParams: {
                projectId: params.projectId,
                threadId: params.threadId,
            },
        })
    )
}

/** POST GCB2/chat/threadMessages */
export async function getChatThreadMessages(params: {
    projectId: string
    threadId: string
    limit?: number
    beforeMessageId?: string
    beforeCreatedAt?: string
}) {
    const url = `GCB2/chat/threadMessages`
    const json: Record<string, unknown> = {
        projectId: params.projectId,
        threadId: params.threadId,
    }
    if (params.limit != null) json.limit = params.limit
    if (params.beforeMessageId) json.beforeMessageId = params.beforeMessageId
    if (params.beforeCreatedAt) json.beforeCreatedAt = params.beforeCreatedAt
    return apiCall<DhChatThreadMessagesResponse>(() => http.post(url, { json }))
}

/** POST GCB2/chat/threads/state */
export async function setChatThreadsState(params: DhChatThreadStateParams) {
    const url = `GCB2/chat/threads/state`
    const json: Record<string, unknown> = {
        projectId: params.projectId,
        threadIds: params.threadIds,
    }
    if (params.pinned != null) json.pinned = params.pinned
    if (params.archived != null) json.archived = params.archived
    if (params.manualUnread != null) json.manualUnread = params.manualUnread
    return apiCall<{ success: boolean }>(() => http.post(url, { json }))
}

/** POST GCB2/chat/threads/read */
export async function markChatThreadsRead(params: DhChatThreadsReadParams) {
    const url = `GCB2/chat/threads/read`
    return apiCall<{ success: boolean; chatsMarked: number }>(() =>
        http.post(url, {
            json: {
                projectId: params.projectId,
                threadIds: params.threadIds,
                read: params.read,
            },
        })
    )
}

/** GET GCB2/chat/contacts */
export async function getChatContacts(params: { projectId: string; q: string; limit?: number }) {
    const url = `GCB2/chat/contacts`
    const searchParams: Record<string, string> = {
        projectId: params.projectId,
        q: params.q,
    }
    if (params.limit != null) searchParams.limit = String(params.limit)
    return apiCall<DhChatContact[]>(() => http.get(url, { searchParams }))
}

/** POST GCB2/chat/sendFirst */
export async function sendFirstChatMessage(params: DhChatSendFirstParams) {
    const url = `GCB2/chat/sendFirst`
    const json: Record<string, unknown> = {
        projectId: params.projectId,
        connectorId: params.connectorId,
    }
    if (params.phone) json.phone = params.phone
    if (params.clientId) json.clientId = params.clientId
    if (params.clientName && !params.clientId) json.clientName = params.clientName
    if (params.text) json.text = params.text
    if (params.media) json.media = params.media
    return apiCall<DhChatSendFirstResponse>(() => http.post(url, { json }))
}

/** POST GCB2/chat/resolveJob */
export async function resolveChatJob(params: { projectId: string; queueJobId: string }) {
    const url = `GCB2/chat/resolveJob`
    return apiCall<DhChatResolveJobResponse>(() =>
        http.post(url, {
            json: {
                projectId: params.projectId,
                queueJobId: params.queueJobId,
            },
        })
    )
}

/** GET GCB2/chat/scope */
export async function getChatScope(params: { projectId: string }) {
    const url = `GCB2/chat/scope`
    return apiCall<DhChatScope>(() =>
        http.get(url, {
            searchParams: {
                projectId: params.projectId,
            },
        })
    )
}
